import { createAction, createReducer } from '@reduxjs/toolkit';
import { preloadedState } from './filters.preloaded-state';
import {
  addAndOpenFilterAction,
  addFilterAction,
  clearFiltersAction,
  closeFilterAction,
  onFilterChangeAction,
  removeFilterAction,
  resetSelectedAction,
  setCandidateFiltersOptionsAction,
  setCurrentTabAction,
  setPagableAction,
  setPageSortAction,
  setPreofferFiltersOptionsAction,
  setRequestFiltersOptionsAction,
  setSelectedAction,
  setVacancyFiltersOptionsAction,
  toggleFilterAction,
} from './fiters.actions';

import {
  candidateStatusFiltersMapper,
  citiesOptionsFilter,
  nestedSelectFilterMapper,
  selectFilterMapper,
} from './filters.mappers';
import { FiltersState } from './filters.types';
import { getAllCandidatesIntervalFiltersThunk, getInitialCandidatesFilterThunk } from './filters.thunks';
import { logoutAction } from '../common';
import { CurrencyEnum, IntervalFiltersType, defaultCurrency } from '@constants';
import { IntervalFiltersValues, NumberRangeFilterType } from '@innowise-group/core';

const prioritizeCandidatesFulfilledAction = createAction('candidates/prioritize/fulfilled');

// The filters state outlives the list page, so bounds that were already loaded are refreshed instead of skipped.
// A range edge that still equals the old bound was not moved by the user and follows the new one.
const applyNumberRangeBounds = (filterState: NumberRangeFilterType['state'], minValue: number, maxValue: number) => {
  const { initialDataLoaded, minValue: previousMin, maxValue: previousMax } = filterState;

  if (!initialDataLoaded || filterState.from === previousMin) filterState.from = minValue;
  if (!initialDataLoaded || filterState.to === previousMax) filterState.to = maxValue;
  filterState.initialDataLoaded = true;
  filterState.minValue = minValue;
  filterState.maxValue = maxValue;
};

const applyIntervalBounds = (
  state: FiltersState,
  intervalType: IntervalFiltersType,
  bounds: IntervalFiltersValues,
  currency?: CurrencyEnum,
) => {
  if (intervalType === IntervalFiltersType.Age) {
    applyNumberRangeBounds(state.candidate.age.state, Number(bounds.min), Number(bounds.max));
  }
  if (intervalType === IntervalFiltersType.OverallExperience) {
    applyNumberRangeBounds(
      state.candidate.overallJobExperience.state,
      Math.round(Number(bounds.min) / 12),
      Math.ceil(Number(bounds.max) / 12),
    );
  }
  if (intervalType === IntervalFiltersType.ItExperience) {
    applyNumberRangeBounds(
      state.candidate.itSphereJobExperience.state,
      Math.round(Number(bounds.min) / 12),
      Math.ceil(Number(bounds.max) / 12),
    );
  }
  if (intervalType === IntervalFiltersType.BirthDate) {
    state.candidate.birthDate.state.min = bounds.min;
    state.candidate.birthDate.state.max = bounds.max;
  }
  if (intervalType === IntervalFiltersType.Salary && currency) {
    const salary = state.candidate.salary.state;
    const previous = salary.variants[currency];
    const min = Math.floor(parseFloat(bounds.min));
    const max = Math.round(parseFloat(bounds.max));
    const isSelectedCurrency = currency === salary.currency;

    salary.variants[currency] = { min, max, initialDataLoaded: true };
    if (!previous?.initialDataLoaded) {
      // Bounds of another currency are loaded when it is selected, so they become the selected range too.
      if (currency === defaultCurrency || isSelectedCurrency) {
        salary.currency = currency;
        salary.from = min;
        salary.to = max;
      }
    } else if (isSelectedCurrency) {
      if (salary.from === previous.min) salary.from = min;
      if (salary.to === previous.max) salary.to = max;
    }
  }
};

export const filtersReducer = createReducer(preloadedState, (builder) =>
  builder
    .addCase(clearFiltersAction, (state, { payload }) => {
      Object.entries(preloadedState).forEach(([key]) => {
        if (!payload?.includes(key as keyof FiltersState)) {
          state[key] = preloadedState[key];
        }
      });
    })

    .addCase(setCandidateFiltersOptionsAction, (state, { payload }) => {
      const { profileId, options: data, reset } = payload;
      // TODO: Remove fix (find the source of empty options)
      const isEmpty = Object.values(data).reduce((acc, val) => {
        if (!val) return true;
        return acc;
      }, false);
      if (isEmpty) return;
      // TODO: Remove fix (find the source of empty options)
      if (reset) {
        state.pagable.currentPage = 1;
      }
      Object.keys(state.candidate).forEach((key) => {
        if (reset) {
          state.pagable.currentPage = 1;
          state.candidate[key] = preloadedState.candidate[key];
        } else {
          state.candidate[key].isOpen = false;
        }
      });
      if (data && profileId) {
        state.candidateInitialOptions = data;
        state.responsible = profileId;
        state.candidateOptions = data;
        state.candidate.statuses = candidateStatusFiltersMapper(state.candidate.statuses, data.statusOptions, reset);
        state.candidate.actualStatuses = candidateStatusFiltersMapper(
          state.candidate.actualStatuses,
          data.statusOptions,
          reset,
        );
        state.candidate.gender = selectFilterMapper(state.candidate.gender, data.genderOptions, reset);
        state.candidate.domains = selectFilterMapper(state.candidate.domains, data.domainOptions, reset);
        state.candidate.countries = selectFilterMapper(state.candidate.countries, data.countryOptions, reset);
        state.candidate.cities = selectFilterMapper(state.candidate.cities, data.citiesOptions, reset);
        state.candidate.visaTypes = selectFilterMapper(state.candidate.visaTypes, data.visaTypesOptions, reset);
        state.candidate.citizenship = selectFilterMapper(state.candidate.citizenship, data.countryOptions, reset);
        state.candidate.workFormats = selectFilterMapper(
          state.candidate.workFormats,
          [...data.workFormatsOptions, ...data.workloadsOptions, ...data.employmentFormsOptions],
          reset,
        );
        state.candidate.sourceFilter = nestedSelectFilterMapper(
          state.candidate.sourceFilter,
          data.candidateSourceOptions,
          data.candidateSourceClarificationOptions,
          reset,
        );
        state.candidate.technologies = selectFilterMapper(state.candidate.technologies, data.technologyOptions, reset);
        state.candidate.relocationCountries = selectFilterMapper(
          state.candidate.relocationCountries,
          data.countryOptions,
          reset,
        );
        state.candidate.professionFilter = nestedSelectFilterMapper(
          state.candidate.professionFilter,
          data.professionOptions,
          data.gradeOptions,
          reset,
        );
        state.candidate.languageFilter = nestedSelectFilterMapper(
          state.candidate.languageFilter,
          data.languageOptions,
          data.languageLevelOptions,
          reset,
        );
        state.candidate.internationalCertificates = selectFilterMapper(
          state.candidate.internationalCertificates,
          data.certificateOptions,
          reset,
        );
      }
    })

    .addCase(setRequestFiltersOptionsAction, (state, { payload }) => {
      const { profileId, options: data, reset } = payload;
      // TODO: Remove fix (find the source of empty options)
      const isEmpty = Object.values(data).reduce((acc, val) => {
        if (!val) return true;
        return acc;
      }, false);
      if (isEmpty) return;
      // TODO: Remove fix (find the source of empty options)
      Object.keys(state.request).forEach((key) => {
        if (reset) {
          state.pagable.currentPage = 1;
          state.request[key] = preloadedState.request[key];
        } else {
          state.request[key].isOpen = false;
        }
      });
      if (data && profileId) {
        state.responsible = profileId;
        state.request.hiringPurpose = selectFilterMapper(
          state.request.hiringPurpose,
          data.vacancyPurposeOptions,
          reset,
        );
        state.request.employmentForms = selectFilterMapper(
          state.request.employmentForms,
          data.employmentFormsOptions,
          reset,
        );
        state.request.structuralUnitId = selectFilterMapper(state.request.structuralUnitId, data.unitsOptions, reset);
        state.request.countries = selectFilterMapper(state.request.countries, data.countryOptions, reset);
        state.request.officeIds = selectFilterMapper(state.request.officeIds, data.officeOptions, reset);
        state.request.statuses = selectFilterMapper(state.request.statuses, data.requestFilterStatusOptions, reset);
        state.request.priority = selectFilterMapper(state.request.priority, data.priorityOptions, reset);
        state.request.isOverdue = selectFilterMapper(state.request.isOverdue, data.overdueOptions, reset);
        state.request.workFormats = selectFilterMapper(state.request.workFormats, data.workFormatsOptions, reset);
        state.request.workload = selectFilterMapper(state.request.workload, data.workloadsOptions, reset);
      }
    })

    .addCase(setVacancyFiltersOptionsAction, (state, { payload }) => {
      const { profileId, options: data, reset } = payload;
      // TODO: Remove fix (find the source of empty options)
      const isEmpty = Object.values(data).reduce((acc, val) => {
        if (!val) return true;
        return acc;
      }, false);
      if (isEmpty) return;
      // TODO: Remove fix (find the source of empty options)
      Object.keys(state.vacancy).forEach((key) => {
        if (reset) {
          state.pagable.currentPage = 1;
          state.vacancy[key] = preloadedState.vacancy[key];
        } else {
          state.vacancy[key].isOpen = false;
        }
      });
      state.responsible = profileId;
      state.vacancy.countries = selectFilterMapper(state.vacancy.countries, data.countryOptions, reset);
      state.vacancy.domainIds = selectFilterMapper(state.vacancy.domainIds, data.domainOptions, reset);
      state.vacancy.languageFilter = nestedSelectFilterMapper(
        state.vacancy.languageFilter,
        data.languageOptions,
        data.languageLevelOptions,
        reset,
      );
      state.vacancy.professionFilter = nestedSelectFilterMapper(
        state.vacancy.professionFilter,
        data.professionOptions,
        data.gradeOptions,
        reset,
      );
      state.vacancy.statusIds = selectFilterMapper(state.vacancy.statusIds, data.vacancyStatus, reset);
      state.vacancy.technologyIds = selectFilterMapper(state.vacancy.technologyIds, data.technologyOptions, reset);
      state.vacancy.hiringPurpose = selectFilterMapper(state.vacancy.hiringPurpose, data.vacancyPurposeOptions, reset);
    })

    .addCase(setPreofferFiltersOptionsAction, (state, { payload }) => {
      const { profileId, options: data, reset } = payload;
      // TODO: Remove fix (find the source of empty options)
      const isEmpty = Object.values(data).reduce((acc, val) => {
        if (!val) return true;

        return acc;
      }, false);

      if (isEmpty) return;
      // TODO: Remove fix (find the source of empty options)
      Object.keys(state.preoffer).forEach((key) => {
        if (reset) {
          state.pagable.currentPage = 1;
          state.preoffer[key] = preloadedState.preoffer[key];
        } else {
          state.preoffer[key].isOpen = false;
        }
      });
      state.responsible = profileId;
      state.preoffer.position = selectFilterMapper(state.preoffer.position, data.professionOptions, reset);
      state.preoffer.grade = selectFilterMapper(state.preoffer.grade, data.gradeOptions, reset);
      state.preoffer.unit = selectFilterMapper(state.preoffer.unit, data.unitOptions, reset);
      state.preoffer.department = selectFilterMapper(state.preoffer.department, data.departmentOptions, reset);
      state.preoffer.division = selectFilterMapper(state.preoffer.division, data.divisionOptions, reset);
    })

    .addCase(addAndOpenFilterAction, (state, { payload }) => {
      state[payload.entity][payload.filter] = {
        ...preloadedState[payload.entity][payload.filter],
        ...(!state[payload.entity][payload.filter].active && {
          active: true,
          isOpen: true,
        }),
        state: state[payload.entity][payload.filter].state,
      };
    })
    .addCase(addFilterAction, (state, { payload }) => {
      state[payload.entity][payload.filter].active = true;
    })
    .addCase(closeFilterAction, (state, { payload }) => {
      if (payload.filter === 'countries' && payload.entity === 'candidate') {
        state.candidate.cities = citiesOptionsFilter(state);
      }
      state[payload.entity][payload.filter].isOpen = false;
    })
    .addCase(removeFilterAction, (state, { payload }) => {
      state[payload.entity][payload.filter].active = false;
      state[payload.entity][payload.filter].isOpen = false;
      state[payload.entity][payload.filter].state = payload.updatedState;
      if (payload.filter === 'countries' && payload.entity === 'candidate') {
        state.candidate.cities = citiesOptionsFilter(state);
      }
    })
    .addCase(toggleFilterAction, (state, { payload }) => {
      state[payload.entity][payload.filter].isOpen = !state[payload.entity][payload.filter].isOpen;
    })
    .addCase(onFilterChangeAction, (state, { payload }) => {
      state[payload.entity][payload.filter].state = payload.updatedState;
    })

    .addCase(setPagableAction, (state, { payload }) => {
      if (payload?.currentPage) state.pagable.currentPage = payload.currentPage;
      if (payload?.pageSize) state.pagable.pageSize = payload.pageSize;
    })
    .addCase(setSelectedAction, (state, { payload }) => {
      state.selected = state.selected.includes(payload)
        ? state.selected.filter((id) => id !== payload)
        : [...state.selected, payload];
    })
    .addCase(resetSelectedAction, (state) => {
      state.selected = preloadedState.selected;
    })
    .addCase(setCurrentTabAction, (state, { payload }) => {
      state.currentTab = payload;
      state.pagable.currentPage = 1;
    })
    .addCase(setPageSortAction, (state, { payload }) => {
      state.sort[payload.entity] = payload.value;
    })
    .addCase(prioritizeCandidatesFulfilledAction, (state) => {
      state.selected = [];
    })

    .addCase(getInitialCandidatesFilterThunk.pending, (state) => {
      state.isLoading = true;
    })
    .addCase(getInitialCandidatesFilterThunk.fulfilled, (state, { payload, meta }) => {
      state.isLoading = false;
      applyIntervalBounds(
        state,
        meta.arg.intervalType,
        payload,
        'currency' in meta.arg ? meta.arg.currency : undefined,
      );
    })
    .addCase(getInitialCandidatesFilterThunk.rejected, (state) => {
      state.isLoading = false;
    })

    // Does not touch the shared `isLoading`: the candidates table must not wait for the bounds.
    .addCase(getAllCandidatesIntervalFiltersThunk.fulfilled, (state, { payload, meta }) => {
      // Only interval types known to the client are applied, extra keys (e.g. STATUS_DATE) are skipped.
      Object.values(IntervalFiltersType).forEach((intervalType) => {
        const bounds = payload?.[intervalType];
        if (bounds) {
          applyIntervalBounds(state, intervalType, bounds, meta.arg.currency);
        }
      });

      // The response has salary in one currency only: the bounds loaded earlier for the others are outdated now
      // and are requested again when such a currency is selected.
      if (payload?.[IntervalFiltersType.Salary]) {
        Object.values(CurrencyEnum)
          .filter((currency) => currency !== meta.arg.currency)
          .forEach((currency) => {
            const variant = state.candidate.salary.state.variants[currency];
            if (variant) variant.initialDataLoaded = false;
          });
      }
    })

    .addCase(logoutAction, () => preloadedState),
);
